/**
 * Enhanced Supabase Adapter
 * Provides database operations with error handling and performance tracking
 */

import { supabase } from '@/integrations/supabase/client';
import type { SyncResult } from './types';
import type { Database } from '@/integrations/supabase/types';
import { performanceMonitor } from '@/lib/utils/performanceMonitor';

type TableNames = keyof Database['public']['Tables'];

export class SupabaseAdapter<T extends { id: string; created_at: string; updated_at?: string }> {
  private tableName: TableNames;

  constructor(tableName: TableNames) {
    this.tableName = tableName;
  }

  async getAll(columns: string = '*'): Promise<SyncResult<T[]>> {
    const startTime = performance.now();
    try {
      const query = supabase
        .from(this.tableName as unknown)
        .select(columns)
        .order('created_at', { ascending: false });

      const { data, error } = await query;

      const duration = performance.now() - startTime;
      performanceMonitor.trackDBQuery(`SELECT ${columns} FROM ${this.tableName} ORDER BY created_at DESC`, duration, this.tableName);

      if (error) throw error;

      return {
        success: true,
        data: (data || []) as unknown as T[],
        synced_at: new Date().toISOString(),
      };
    } catch (error: unknown) {
      const duration = performance.now() - startTime;
      performanceMonitor.trackDBQuery(`SELECT ${columns} FROM ${this.tableName} (FAILED)`, duration, this.tableName);

      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error(`[Supabase] Error fetching from ${this.tableName}:`, error);
      return { success: false, error: message };
    }
  }

  async getById(id: string, column: string = 'id', columns: string = '*'): Promise<SyncResult<T>> {
    const startTime = performance.now();
    try {
      const query = supabase
        .from(this.tableName as unknown)
        .select(columns)
        .eq(column, id);

      const { data, error } = await query.maybeSingle();

      const duration = performance.now() - startTime;
      performanceMonitor.trackDBQuery(`SELECT * FROM ${this.tableName} WHERE ${column} = '${id}'`, duration, this.tableName);

      if (error) throw error;

      return {
        success: true,
        data: data as unknown as T,
        synced_at: new Date().toISOString(),
      };
    } catch (error: unknown) {
      const duration = performance.now() - startTime;
      performanceMonitor.trackDBQuery(`SELECT * FROM ${this.tableName} WHERE ${column} = '${id}' (FAILED)`, duration, this.tableName);

      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error(`[Supabase] Error fetching ${id} from ${this.tableName} (via ${column}):`, error);
      return { success: false, error: message };
    }
  }

  async create(item: Omit<T, 'id' | 'created_at' | 'updated_at'>): Promise<SyncResult<T>> {
    try {
      const { data, error } = await supabase
        .from(this.tableName as unknown)
        .insert(item as never)
        .select()
        .maybeSingle();

      if (error) throw error;

      return {
        success: true,
        data: data as unknown as T,
        synced_at: new Date().toISOString(),
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error(`[Supabase] Error creating in ${this.tableName}:`, error);
      return { success: false, error: message };
    }
  }

  async update(id: string, updates: Partial<T>): Promise<SyncResult<T>> {
    try {
      const { data, error } = await supabase
        .from(this.tableName as unknown)
        .update(updates as never)
        .eq('id', id)
        .select()
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        return { success: false, error: `Record ${id} not found in ${this.tableName}` };
      }

      return {
        success: true,
        data: data as unknown as T,
        synced_at: new Date().toISOString(),
      };
    } catch (error: unknown) {
      const err = error as { message?: string; code?: string; details?: string; hint?: string };
      const message = err.message || 'Unknown error';
      console.error(`[Supabase] Error updating ${id} in ${this.tableName}:`, error);
      return { success: false, error: message };
    }
  }

  async upsert(item: T, onConflict?: string): Promise<SyncResult<T>> {
    try {
      const query = supabase.from(this.tableName as unknown).upsert(item as never, { onConflict });

      const { data, error } = await query
        .select()
        .maybeSingle();

      if (error) throw error;

      return {
        success: true,
        data: data as unknown as T,
        synced_at: new Date().toISOString(),
      };
    } catch (error: unknown) {
      const err = error as { message?: string; code?: string; details?: string; hint?: string };
      const message = err.message || 'Unknown error';
      console.error(`[Supabase] Error upserting in ${this.tableName}:`, error);
      return { success: false, error: message };
    }
  }

  async bulkUpsert(items: T[]): Promise<SyncResult<T[]>> {
    if (items.length === 0) {
      return { success: true, data: [], synced_at: new Date().toISOString() };
    }

    try {
      const { data, error } = await supabase
        .from(this.tableName as unknown)
        .upsert(items as never[])
        .select();

      if (error) throw error;

      return {
        success: true,
        data: (data || []) as unknown as T[],
        synced_at: new Date().toISOString(),
      };
    } catch (error: unknown) {
      const err = error as { message?: string; code?: string; details?: string; hint?: string };
      const message = err.message || 'Unknown error';
      console.error(`[Supabase] Error bulk upserting in ${this.tableName}:`, error);
      return { success: false, error: message };
    }
  }

  async delete(id: string): Promise<SyncResult<boolean>> {
    try {
      const { error } = await supabase
        .from(this.tableName as unknown)
        .delete()
        .eq('id', id);

      if (error) throw error;

      return {
        success: true,
        data: true,
        synced_at: new Date().toISOString(),
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error(`[Supabase] Error deleting ${id} from ${this.tableName}:`, error);
      return { success: false, error: message };
    }
  }

  async bulkDelete(ids: string[]): Promise<SyncResult<boolean>> {
    if (ids.length === 0) return { success: true, data: true };
    try {
      const { error } = await supabase
        .from(this.tableName as unknown)
        .delete()
        .in('id', ids);

      if (error) throw error;

      return {
        success: true,
        data: true,
        synced_at: new Date().toISOString(),
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error(`[Supabase] Error bulk deleting from ${this.tableName}:`, error);
      return { success: false, error: message };
    }
  }
}
